import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Activity, MapPin, Search, Trophy, Wind, Thermometer, Droplets, AlertTriangle, Navigation } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SPORT_OPTIONS = ["baseball", "softball", "soccer", "football", "golf", "tennis", "pickleball", "basketball"];

function tone(score, isDarkMode) {
  if (score >= 8) return isDarkMode ? "text-emerald-300" : "text-emerald-700";
  if (score >= 6) return isDarkMode ? "text-lime-300" : "text-lime-700";
  if (score >= 4) return isDarkMode ? "text-amber-300" : "text-amber-700";
  return isDarkMode ? "text-orange-300" : "text-orange-700";
}

function labelSport(value) {
  if (!value) return "Any sport";
  return value.replaceAll("_", " ").replace(/\b\w/g, (char) => char.toUpperCase()).replace("American Football", "Football");
}

export function SportsFieldWeatherCard({ lat, lon, isDarkMode }) {
  const [selectedSport, setSelectedSport] = useState("");
  const [customSport, setCustomSport] = useState("");
  const [locationQuery, setLocationQuery] = useState("");
  const [manualField, setManualField] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const activeSport = useMemo(() => customSport.trim() || selectedSport, [customSport, selectedSport]);

  const fetchSportsWeather = async (overrides = {}) => {
    if (!lat || !lon) return;
    setLoading(true);
    setError("");
    try {
      const params = {
        lat,
        lon,
        sport: overrides.sport ?? (activeSport || undefined),
        location_query: overrides.locationQuery ?? (locationQuery.trim() || undefined),
        manual_field_name: overrides.manualField ?? (manualField.trim() || undefined),
        selected_field_lat: overrides.fieldLat,
        selected_field_lon: overrides.fieldLon,
      };
      const response = await axios.get(`${API}/weather/sports/field`, { params, timeout: 18000 });
      setData(response.data);
    } catch (requestError) {
      console.error("Sports field weather error:", requestError.message);
      setError("Sports field weather is temporarily unavailable.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSportsWeather({ sport: "" }); }, [lat, lon]);

  const panel = isDarkMode ? "bg-white/5 border-white/10" : "bg-white/80 border-gray-200";
  const muted = isDarkMode ? "text-white/55" : "text-gray-600";
  const field = data?.selected_field;
  const assessment = data?.assessment || {};
  const wx = assessment.weather || {};

  return (
    <div className="glass rounded-3xl p-5" data-testid="sports-field-weather-card">
      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-5 min-h-[390px]">
        <aside className={`rounded-2xl border p-4 ${panel}`} data-testid="sports-field-selection-panel">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-5 h-5 text-[#ccff00]" />
            <div>
              <h3 className={`font-bold ${isDarkMode ? "text-white/85" : "text-gray-900"}`}>Sports Field Weather</h3>
              <p className={`text-xs ${muted}`}>Nearest field by default</p>
            </div>
          </div>

          <label className={`text-xs font-semibold ${muted}`} htmlFor="sports-card-sport-select">Sport</label>
          <select
            id="sports-card-sport-select"
            data-testid="sports-card-sport-select"
            value={selectedSport}
            onChange={(event) => { setSelectedSport(event.target.value); setCustomSport(""); }}
            className={`w-full mt-1 mb-3 rounded-xl px-3 py-2 text-sm border ${isDarkMode ? "bg-black/30 border-white/10 text-white" : "bg-white border-gray-200 text-gray-900"}`}
          >
            <option value="">Any sport</option>
            {SPORT_OPTIONS.map((sport) => <option key={sport} value={sport}>{labelSport(sport)}</option>)}
          </select>

          <label className={`text-xs font-semibold ${muted}`} htmlFor="sports-card-custom-sport-input">Add sport</label>
          <input
            id="sports-card-custom-sport-input"
            data-testid="sports-card-custom-sport-input"
            value={customSport}
            onChange={(event) => setCustomSport(event.target.value)}
            placeholder="e.g. lacrosse"
            className={`w-full mt-1 mb-3 rounded-xl px-3 py-2 text-sm border ${isDarkMode ? "bg-black/30 border-white/10 text-white placeholder:text-white/35" : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400"}`}
          />

          <label className={`text-xs font-semibold ${muted}`} htmlFor="sports-card-location-input">Search location</label>
          <input
            id="sports-card-location-input"
            data-testid="sports-card-location-input"
            value={locationQuery}
            onChange={(event) => setLocationQuery(event.target.value)}
            placeholder="City, park, address"
            className={`w-full mt-1 mb-3 rounded-xl px-3 py-2 text-sm border ${isDarkMode ? "bg-black/30 border-white/10 text-white placeholder:text-white/35" : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400"}`}
          />

          <label className={`text-xs font-semibold ${muted}`} htmlFor="sports-card-manual-field-input">Manual field</label>
          <input
            id="sports-card-manual-field-input"
            data-testid="sports-card-manual-field-input"
            value={manualField}
            onChange={(event) => setManualField(event.target.value)}
            placeholder="Field name optional"
            className={`w-full mt-1 mb-4 rounded-xl px-3 py-2 text-sm border ${isDarkMode ? "bg-black/30 border-white/10 text-white placeholder:text-white/35" : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400"}`}
          />

          <button
            type="button"
            data-testid="sports-card-search-button"
            onClick={() => fetchSportsWeather()}
            disabled={loading}
            className="w-full rounded-xl bg-[#ccff00] text-black font-bold py-2.5 text-sm hover:bg-[#d9ff33] transition-colors disabled:opacity-60"
          >
            {loading ? "Checking..." : "Update sport weather"}
          </button>
          {error && <p className="mt-3 text-xs text-orange-400" data-testid="sports-card-error-message">{error}</p>}
        </aside>

        <section className={`rounded-2xl border p-4 ${panel}`} data-testid="sports-field-detail-panel">
          {loading && !data ? (
            <div className="h-full animate-pulse space-y-4" data-testid="sports-card-loading">
              <div className={`h-8 rounded-xl w-2/3 ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} />
              <div className="grid grid-cols-3 gap-3"><div className={`h-20 rounded-xl ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} /><div className={`h-20 rounded-xl ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} /><div className={`h-20 rounded-xl ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} /></div>
              <div className={`h-32 rounded-xl ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className={`text-xs font-semibold ${isDarkMode ? "text-[#ccff00]" : "text-lime-700"}`}>{data?.selected_sport_label || "Any Sport"}</p>
                  <h3 className={`text-xl font-black truncate ${isDarkMode ? "text-white" : "text-gray-900"}`} data-testid="sports-card-field-name">{field?.name || "Finding nearest sport field"}</h3>
                  <p className={`text-xs ${muted}`} data-testid="sports-card-field-meta">{field ? `${field.distance_mi} mi · ${labelSport(field.sport)} · ${field.surface}` : "Current conditions at your location"}</p>
                </div>
                <div className="text-right shrink-0" data-testid="sports-card-play-score">
                  <p className={`text-4xl font-black leading-none ${tone(assessment.score, isDarkMode)}`}>{assessment.score || "—"}</p>
                  <p className={`text-[11px] font-bold ${muted}`}>{assessment.rating || "Loading"}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="sports-card-current-weather-grid">
                <div className={`rounded-xl border p-3 ${panel}`}><Thermometer className="w-4 h-4 text-orange-400 mb-1" /><p className="text-xs opacity-60">Temp</p><p className="font-bold">{wx.air_temp_f ?? "—"}°F</p></div>
                <div className={`rounded-xl border p-3 ${panel}`}><Wind className="w-4 h-4 text-cyan-400 mb-1" /><p className="text-xs opacity-60">Wind</p><p className="font-bold">{wx.wind_speed_mph ?? "—"} mph</p></div>
                <div className={`rounded-xl border p-3 ${panel}`}><Droplets className="w-4 h-4 text-blue-400 mb-1" /><p className="text-xs opacity-60">Humidity</p><p className="font-bold">{wx.humidity ?? "—"}%</p></div>
                <div className={`rounded-xl border p-3 ${panel}`}><Navigation className="w-4 h-4 text-lime-400 mb-1" /><p className="text-xs opacity-60">Visibility</p><p className="font-bold">{wx.visibility_mi ?? "—"} mi</p></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="sports-card-guidance-grid">
                <div className={`rounded-xl border p-3 ${panel}`}>
                  <div className="flex items-center gap-2 mb-2"><Activity className="w-4 h-4 text-[#ccff00]" /><span className="text-xs font-semibold">Play factors</span></div>
                  {(assessment.strengths || []).slice(0, 4).map((row) => <p key={row} className={`text-xs mb-1 ${muted}`}>• {row}</p>)}
                </div>
                <div className={`rounded-xl border p-3 ${panel}`}>
                  <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-amber-400" /><span className="text-xs font-semibold">Sport notes</span></div>
                  {(assessment.cautions?.length ? assessment.cautions : assessment.field_notes || []).slice(0, 4).map((row) => <p key={row} className={`text-xs mb-1 ${muted}`}>• {row}</p>)}
                </div>
              </div>

              <div className={`rounded-xl border p-3 ${panel}`} data-testid="sports-card-nearby-fields-list">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#00f2ea]" /><span className="text-xs font-semibold">Nearby fields</span></div>
                  <span className={`text-[11px] ${muted}`}>{data?.attribution}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {(data?.nearby_fields || []).slice(0, 4).map((nearby) => (
                    <button
                      key={nearby.id}
                      type="button"
                      data-testid={`sports-card-nearby-field-${nearby.id.replace(/[^a-zA-Z0-9-]/g, '-')}`}
                      className={`text-left rounded-lg px-2 py-1.5 text-xs transition-colors ${isDarkMode ? "bg-white/5 hover:bg-white/10" : "bg-gray-50 hover:bg-gray-100"}`}
                      onClick={(event) => { event.stopPropagation(); setManualField(nearby.name); fetchSportsWeather({ manualField: nearby.name, sport: nearby.sport, fieldLat: nearby.lat, fieldLon: nearby.lon }); }}
                    >
                      <span className="font-semibold truncate block">{nearby.name}</span>
                      <span className={muted}>{nearby.distance_mi} mi · {labelSport(nearby.sport)}</span>
                    </button>
                  ))}
                  {(!data?.nearby_fields || data.nearby_fields.length === 0) && <p className={`text-xs ${muted}`}>No mapped fields found nearby. Manual field entry can still use current conditions.</p>}
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}